// DO NOT EDIT -- generated from fs/services/capnhook/events.proto

import * as fullstory_operations_v1 from '../../api/fullstoryapis/operations/v1';
import * as protobuf from '../../../google/protobuf';

/** Notifies when a user of FullStory makes a note on a recorded session. */
export interface NoteCreated {
  v1: NoteCreatedV1 | null;
}

export interface NoteCreatedV1 {
  /** Unique key for note event */
  id: string;
  /** When the note was created. */
  created: protobuf.timestamp | null;
  /** Email of the user who created the note. */
  author: string;
  /** Text in the note. */
  text: string;
  /** The URL corresponding to the session on which the note was created. */
  sessionUrl: string;
  /** The URL corresponding to the user. */
  userUrl: string;
  /** The short link used for sharing. Not every note will have a share link. */
  shareLink: string;
  pageInfo: NoteCreatedV1.PageInfo | null;
  /** The time that the note is referring to in the session */
  notedTime: protobuf.timestamp | null;
}

export namespace NoteCreatedV1 {
  export interface PageInfo {
    /** The URL of the page in session on which the note was taken. */
    pageUrl: string;
    /** The IP address corresponding to the user's session. (May be empty depending on privacy settings) */
    ipAddress: string;
    /** The User Agent for the session. */
    userAgent: string;
    /** The referrer address for the session. */
    referrer: string;
    /** The country of origin for the session. */
    country: string;
    /** The height of the page in pixels. */
    pageHeight: number;
    /** The width of the page in pixels. */
    pageWidth: number;
  }
}

/** TODO(sarahgrace): Remove this completely once the changes to not use it are deployed. */
export interface OrgQuotaStatus {
  v1: OrgQuotaStatusV1 | null;
}

export interface OrgQuotaStatusV1 {
  /** When the quota update was calculated */
  timestamp: protobuf.timestamp | null;
  /** Session quota for the org */
  sessionQuota: OrgQuotaStatusV1.Quota | null;
  /** Defines the date span for the org's current cycle */
  quotaCycle: OrgQuotaStatusV1.QuotaCycle | null;
}

export namespace OrgQuotaStatusV1 {
  /** Generic resource quota status */
  export interface Quota {
    /** Defined max value for this resource */
    limit: protobuf.int64;
    /** Most recently observed value of this resource */
    consumed: protobuf.int64;
  }

  export interface QuotaCycle {
    start: protobuf.timestamp | null;
    end: protobuf.timestamp | null;
  }
}

/** Notifies when a user of FullStory creates a new segment. */
export interface SegmentCreated {
  v1: SegmentCreatedV1 | null;
}

export interface SegmentCreatedV1 {
  /** Unique identifier for the segment. */
  id: string;
  /** The email for the user that created the segment. */
  creator: string;
  /** The name of the segment. */
  name: string;
  /** The url for the segment */
  url: string;
  /** When the segment was created. */
  created: protobuf.timestamp | null;
}

/**
 * Notifies when a metric alert triggers.
 * 
 * The `"timestamp"` field will hold the time at which the alert triggered, the `"value"` field will hold the
 * value of the metric at the time that the alert triggered, and the `"notification_url"` field will hold a URL where
 * the triggered alert can be viewed in Fullstory.
 * 
 * _Alerts is a paid feature. See [pricing page](https://www.fullstory.com/plans) for more details._
 */
export interface MetricAlert {
  /**
   * sub-event field must be at top-level (but should also be present, in some form,
   * in each of the versioned payloads)
   */
  alertId: string;
  v1: MetricAlertV1 | null;
}

export interface MetricAlertV1 {
  /** Unique identifier for this notification instance. */
  id: string;
  /** Unique identifier for the corresponding alert. */
  alertId: string;
  /** When the alert was fired. */
  timestamp: protobuf.timestamp | null;
  /** The value that exceeded the alert threshold. */
  value: number;
  /** The url for the specific notification. */
  notificationUrl: string;
  alertConfig: MetricAlertV1.AlertConfig | null;
}

export namespace MetricAlertV1 {
  export interface AlertConfig {
    alertName: string;
    alertDescription: string;
    alertCreator: string;
    alertUrl: string;
    metricName: string;
    metricDescription: string;
    metricCreator: string;
    metricUrl: string;
  }
}

/**
 * Notifies when a segment-based alert triggers. Alerts can trigger when the active users in a saved segment cross a configured threshold.
 * 
 * The `"duration"` field in the `"alertConfig"` section of the payload will be `"DAILY"`, `"WEEKLY"`, or `"MONTHLY"`, based on whether the alert was
 * configured to examine daily active users vs. weekly or monthly active users. The `"direction"` field, also in the `"alertConfig"` section, will be
 * either `"ABOVE"` or `"BELOW"`, based on whether the alert triggers when the observed value goes above or below the configured threshold.
 * 
 * _When configuring an endpoint that receives this type of event, you can configure which particular alert(s) will be delivered to the endpoint. If
 * you have not created any alerts, they will not show up as an option when configuring a webhook endpoint._
 * 
 * _Alerts is an Enterprise pricing feature. See [pricing page](https://www.fullstory.com/plans) for more details._
 */
export interface SegmentTrendAlert {
  /**
   * sub-event field must be at top-level (but should also be present, in some form,
   * in each of the versioned payloads)
   */
  alertId: string;
  v1: SegmentTrendAlertV1 | null;
}

export interface SegmentTrendAlertV1 {
  /** Unique identifier for this notification instance. */
  id: string;
  /** Unique identifier for the corresponding alert. */
  alertId: string;
  /** When the alert was fired. */
  timestamp: protobuf.timestamp | null;
  /** The value that exceeded the alert threshold. */
  value: number;
  /** The url for the specific notification. */
  notificationUrl: string;
  alertConfig: SegmentTrendAlertV1.AlertConfig | null;
}

export namespace SegmentTrendAlertV1 {
  export interface AlertConfig {
    /** User description. */
    description: string;
    /** This alert was triggered when the number of users crossed this threshold. */
    threshold: number;
    /** Whether the value is above or below the threshold (based on how the alert was configured). */
    direction: SegmentTrendAlertV1.AlertConfig.Direction;
    /** The time period over which data is considered. */
    duration: SegmentTrendAlertV1.AlertConfig.Duration;
    /** The url for the alert configuration */
    alertUrl: string;
    /** The email for the user that created the alert. */
    alertCreator: string;
    /** The url for the segment */
    segmentUrl: string;
    /** The email for the user that created the segment or empty if it's a standard segment. */
    segmentCreator: string;
    /** The name of the segment. */
    segmentName: string;
  }

  export namespace AlertConfig {
    export enum Direction {
      INVALID_DIRECTION = 0,
      ABOVE = 1,
      BELOW = 2
    }

    export enum Duration {
      INVALID_DURATION = 0,
      DAILY = 1,
      WEEKLY = 2,
      MONTHLY = 3
    }
  }
}

export interface ApikeyGenerated {
  v1: ApikeyGeneratedV1 | null;
}

export interface ApikeyGeneratedV1 {
  /** Email */
  userEmail: string;
  /** Key suffix; helps to uniquely identify the key */
  suffix: string;
  /** When the new key was generated */
  timestamp: protobuf.timestamp | null;
  /** User-provided name for the key */
  name: string;
  /** Security role assigned to the key */
  role: ApikeyGeneratedV1.Role;
}

export namespace ApikeyGeneratedV1 {
  export enum Role {
    UNKNOWN = 0,
    ADMIN = 1,
    STANDARD = 2,
    GUEST = 3,
    UMBRELLA_MANAGER = 4,
    ARCHITECT = 5
  }
}

/**
 * Notifies when a native mobile app has crashed. This event can be captured and recorded by the FullStory instrumentation for Android apps. This
 * event is generated when the crash event is processed by FullStory servers.
 * 
 * _Access to FullStory Native Mobile is dependent on your FullStory plan. Please contact your Account Executive or support@fullstory.com to learn
 * more._
 */
export interface NativemobileEventCrash {
  v1: NativemobileEventCrashV1 | null;
}

export interface NativemobileEventCrashV1 {
  /** The app name corresponding to the session that crashed. */
  appName: string;
  /** The app's package. */
  appPackageName: string;
  /** The app's version. */
  appVersion: string;
  /** The model of the device. */
  deviceModel: string;
  /** The operating system version of the device. */
  osVersion: string;
  /** The URL corresponding to the session that crashed. */
  sessionUrl: string;
}

/**
 * Notifies when a defined event has been processed. Defined events are created via the web interface.  The payload includes the details of the defined event.
 * 
 * _When configuring an endpoint that receives this type of event, you configure which particular defined event(s) will be delivered to the endpoint.
 * If you have not created any defined events, they will not show up as an option when configuring a webhook endpoint._
 */
export interface RecordingEventDefined {
  /**
   * sub-event field must be at top-level (but should also be present, in some form,
   * in each of the versioned payloads)
   */
  id: string;
  v1: RecordingEventDefinedV1 | null;
}

export interface RecordingEventDefinedV1 {
  /** defined event name (defined in The web app) */
  id: string;
  /** list of errors (since ....) */
  errors: RecordingEventDefinedV1.Error[];
  name: string;
  /** url of recorded page */
  pageUrl: string;
  /** url of session in FS */
  sessionUrl: string;
  /** When the event occurred */
  timestamp: protobuf.timestamp | null;
}

export namespace RecordingEventDefinedV1 {
  export interface Error {
    errCode: string;
    field: string;
    value: string;
  }
}

/**
 * Notifies when a custom event has been processed. Custom events are created during recording via the
 * [FS.event](https://developer.fullstory.com/custom-events) Browser API function. The payload includes the details of the custom event. It also
 * contains any validation errors encountered while processing the any properties added to the custom event.
 * 
 * The `properties` field is a map whose values can be any type. The actual type of the value will depend on the the property key. For example,
 * properties whose key ends in "_str" will have string values; properties whose key ends in "_bool" will have boolean values. More details on the
 * naming conventions and expected value types can be found in the knowledge base.
 * 
 * _When configuring an endpoint that receives this type of event, you configure which particular custom event(s) will be delivered to the endpoint.
 * If you have not created any custom events, they will not show up as an option when configuring a webhook endpoint._
 */
export interface RecordingEventCustom {
  /**
   * sub-event field must be at top-level (but should also be present, in some form,
   * in each of the versioned payloads)
   */
  name: string;
  v1: RecordingEventCustomV1 | null;
}

export interface RecordingEventCustomV1 {
  /** custom event name (defined by client) */
  name: string;
  /** all event properties */
  properties: { [key: string]: protobuf.Value };
  /** list of errors (since ....) */
  errors: RecordingEventCustomV1.Error[];
  /** url of recorded page */
  pageUrl: string;
  /** url of session in FS */
  sessionUrl: string;
  /** When the event occurred */
  timestamp: protobuf.timestamp | null;
}

export namespace RecordingEventCustomV1 {
  export interface Error {
    errCode: string;
    field: string;
    value: string;
  }
}

export interface OperationFinished {
  /** type of the operation */
  type: fullstory_operations_v1.OperationType;
  v1: OperationFinishedV1 | null;
}

export interface OperationFinishedV1 {
  /** id of the operation that finished */
  id: string;
  /** type of the operation */
  type: fullstory_operations_v1.OperationType;
  /** when the operation finished */
  finished: protobuf.timestamp | null;
}
